import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useOrganization } from "@/auth/organization";
import {
  Badge,
  Button,
  EmptyState,
  ErrorAlert,
  Field,
  Form,
  Input,
  Modal,
  Section,
  useToast,
  type Requirement,
} from "@/components/ui";
import { RuleRow } from "@/components/domains/parts";
import {
  domainKeys,
  domainsApi,
  type DnsPreview,
  type Domain,
  type EmailForward,
  type Zone,
} from "@/lib/domains";

import { BatchReviewModal } from "../dns/BatchReviewModal";

interface Draft {
  address: string;
  isCatchall: boolean;
  destination: string;
}

const EMPTY_DRAFT: Draft = { address: "", isCatchall: false, destination: "" };

/** E-mail forwarding (PHP-3942, AC6-8): up to 20 addresses plus one catch-all, and the MX switch itself. */
export function EmailForwardingSection({
  domain,
  canManage,
  manage,
  forwardingOn,
  onZoneApplied,
}: {
  domain: Domain;
  canManage: boolean;
  manage: Requirement;
  /** Whether the domain's MX already points at CortexGrip's forwarding service, or null while unknown. */
  forwardingOn: boolean | null;
  onZoneApplied: (next: Zone) => void | Promise<void>;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const listKey = [...domainKeys.all(orgId), "email-forwards", domain.id];
  const list = useQuery({ queryKey: listKey, queryFn: () => domainsApi.emailForwards(orgId, domain.id) });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<EmailForward | null>(null);
  const [enablePreview, setEnablePreview] = useState<DnsPreview | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);

  const invalidateList = () => qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });

  const create = useMutation({
    mutationFn: (d: Draft) =>
      domainsApi.createEmailForward(orgId, domain.id, {
        destination: d.destination,
        address: d.isCatchall ? null : d.address,
        is_catchall: d.isCatchall,
      }),
    onSuccess: async (created) => {
      await invalidateList();
      toast.show({
        tone: "success",
        title: "Forwarding address added",
        body: `We'll e-mail ${created.destination} to confirm before forwarding starts.`,
      });
      setDraft(null);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => domainsApi.deleteEmailForward(orgId, domain.id, id),
    onSuccess: async () => {
      await invalidateList();
      toast.show({ tone: "success", title: "Forwarding address removed" });
      setRemoving(null);
    },
  });

  const previewEnable = useMutation({
    mutationFn: () => domainsApi.previewEmailForwarding(orgId, domain.id),
    onSuccess: setEnablePreview,
  });
  const enable = useMutation({
    mutationFn: () => domainsApi.enableEmailForwarding(orgId, domain.id),
    onSuccess: async (next) => {
      await onZoneApplied(next);
      toast.show({ tone: "success", title: "Email forwarding turned on" });
      setEnablePreview(null);
    },
  });
  const disable = useMutation({
    mutationFn: () => domainsApi.disableEmailForwarding(orgId, domain.id),
    onSuccess: async (next) => {
      await onZoneApplied(next);
      toast.show({ tone: "success", title: "Email forwarding turned off" });
      setDisableOpen(false);
    },
  });

  const closeDraft = () => {
    if (create.isPending) return;
    setDraft(null);
    create.reset();
  };
  const hasCatchall = list.data?.forwards.some((f) => f.is_catchall) ?? false;
  const atLimit = (list.data?.forwards.filter((f) => !f.is_catchall).length ?? 0) >= 20;

  return (
    <Section
      title="Email forwarding"
      subtitle="Forward mail sent to an address at this domain to another mailbox"
      action={
        <div className="flex gap-2">
          {canManage &&
            (forwardingOn ? (
              <Button size="lg" variant="secondary" requires={manage} onClick={() => setDisableOpen(true)}>
                Turn off
              </Button>
            ) : (
              <Button
                size="lg"
                variant="secondary"
                requires={manage}
                loading={previewEnable.isPending}
                onClick={() => {
                  previewEnable.reset();
                  previewEnable.mutate();
                }}
              >
                Turn on
              </Button>
            ))}
          {canManage && (
            <Button size="lg" variant="brand" requires={manage} onClick={() => setDraft(EMPTY_DRAFT)}>
              Add address
            </Button>
          )}
        </div>
      }
    >
      {!forwardingOn && (
        <p className="text-[13px] text-fg-subtle">
          Email forwarding is off, so addresses below won't receive mail until it's turned on.
        </p>
      )}
      {previewEnable.isError && <ErrorAlert error={previewEnable.error} />}
      {list.isPending ? (
        <p className="text-[13px] text-fg-subtle">Loading…</p>
      ) : list.isError ? (
        <ErrorAlert error={list.error} />
      ) : list.data.forwards.length === 0 ? (
        <EmptyState
          title="No forwarding addresses yet"
          description="Forward mail sent to an address at this domain, like sales, to another mailbox."
          action={
            canManage && (
              <Button variant="secondary" onClick={() => setDraft(EMPTY_DRAFT)}>
                Add address
              </Button>
            )
          }
        />
      ) : (
        list.data.forwards.map((f, i) => (
          <RuleRow key={f.id} first={i === 0}>
            <div className="min-w-0 flex-1">
              <p className="text-sm break-all text-fg">
                {f.is_catchall ? `Every other address @${domain.fqdn}` : `${f.address}@${domain.fqdn}`} →{" "}
                {f.destination}
              </p>
            </div>
            <Badge tone={f.confirmed ? "success" : "warning"} size="sm">
              {f.confirmed ? "Confirmed" : "Pending confirmation"}
            </Badge>
            {canManage && (
              <Button
                size="sm"
                variant="ghost"
                className="text-[#b52522] hover:text-[#b52522]"
                requires={manage}
                onClick={() => setRemoving(f)}
              >
                Remove
              </Button>
            )}
          </RuleRow>
        ))
      )}

      {draft && (
        <Modal
          open
          onClose={closeDraft}
          title="Add a forwarding address"
          footer={
            <>
              <Button variant="secondary" size="lg" onClick={closeDraft}>
                Cancel
              </Button>
              <Button
                variant="brand"
                size="lg"
                type="submit"
                form="email-forward-form"
                loading={create.isPending}
              >
                Add address
              </Button>
            </>
          }
        >
          <Form
            id="email-forward-form"
            className="flex flex-col gap-4"
            error={create.error}
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate(draft);
            }}
          >
            {!draft.isCatchall && (
              <Field label="Address" name="address" required hint={`@${domain.fqdn}`}>
                {(control) => (
                  <Input
                    {...control}
                    value={draft.address}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="sales"
                    onChange={(e) => setDraft({ ...draft, address: e.target.value })}
                  />
                )}
              </Field>
            )}
            <label className="flex items-center gap-2 text-[13px] text-fg">
              <input
                type="checkbox"
                checked={draft.isCatchall}
                disabled={hasCatchall}
                onChange={(e) => setDraft({ ...draft, isCatchall: e.target.checked, address: "" })}
                className="size-4 accent-brand-500"
              />
              Catch-all: forward every address at {domain.fqdn} that isn't listed above
            </label>
            {hasCatchall && !draft.isCatchall && (
              <p className="text-xs text-fg-subtle">This domain already has a catch-all address.</p>
            )}
            {atLimit && !draft.isCatchall && (
              <p className="text-xs text-fg-subtle">
                You can have up to 20 forwarding addresses on a domain.
              </p>
            )}
            <Field label="Destination e-mail" name="destination" required>
              {(control) => (
                <Input
                  {...control}
                  type="email"
                  value={draft.destination}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="you@example.com"
                  onChange={(e) => setDraft({ ...draft, destination: e.target.value })}
                />
              )}
            </Field>
            <p className="text-xs text-fg-subtle">
              We'll e-mail the destination to confirm before forwarding starts.
            </p>
            <ErrorAlert error={create.error} />
          </Form>
        </Modal>
      )}

      {enablePreview && (
        <BatchReviewModal
          open
          fqdn={domain.fqdn}
          title="Turn on email forwarding"
          description={`This replaces ${domain.fqdn}'s mail (MX) records so forwarded addresses start receiving mail.`}
          preview={enablePreview}
          onClose={() => {
            if (enable.isPending) return;
            setEnablePreview(null);
            enable.reset();
          }}
          onConfirm={() => enable.mutate()}
          confirmLabel="Turn on"
          applying={enable.isPending}
          error={enable.error}
        />
      )}

      {disableOpen && (
        <Modal
          open
          onClose={() => {
            if (disable.isPending) return;
            setDisableOpen(false);
            disable.reset();
          }}
          width={420}
          title="Turn off email forwarding?"
          footer={
            <>
              <Button variant="secondary" size="lg" onClick={() => setDisableOpen(false)}>
                Cancel
              </Button>
              <Button variant="danger" size="lg" loading={disable.isPending} onClick={() => disable.mutate()}>
                Turn off
              </Button>
            </>
          }
        >
          <p className="text-[13px] text-[#5c5551]">
            Mail sent to addresses at {domain.fqdn} will stop being forwarded until this is turned back on.
          </p>
          <ErrorAlert error={disable.error} />
        </Modal>
      )}

      {removing && (
        <Modal
          open
          onClose={() => {
            if (remove.isPending) return;
            setRemoving(null);
            remove.reset();
          }}
          width={420}
          title="Remove this forwarding address?"
          note="This can't be undone."
          footer={
            <>
              <Button variant="secondary" size="lg" onClick={() => setRemoving(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="lg"
                loading={remove.isPending}
                onClick={() => remove.mutate(removing.id)}
              >
                Remove address
              </Button>
            </>
          }
        >
          <p className="text-[13px] text-[#5c5551]">
            {removing.is_catchall
              ? `Every other address @${domain.fqdn}`
              : `${removing.address}@${domain.fqdn}`}{" "}
            will stop forwarding to {removing.destination}.
          </p>
          <ErrorAlert error={remove.error} />
        </Modal>
      )}
    </Section>
  );
}
